import React, { useEffect, useRef } from 'react';
import { FileBox, MCButton, MCText, View } from 'src/components/basic';

import { remCss } from '../utils';
import LANG_CONST from 'src/helpers/lang/const';
import { scssVariables, useScreenDetail } from 'src/helpers';
import SVGIcon from 'src/components/svg';
import { MediaGroup, TableLibrary } from 'src/components/complex';
import moment from 'moment';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import galleryModel from '../galleryModel';

const Item = ({ item }) =>
  !item ? (
    <View style={{ flex: 1 }} />
  ) : (
    <View
      style={{
        flex: 1,
        flexDirection: 'row',
        gap: remCss(20),
        marginBottom: remCss(25),
        justifyContent: 'flex-start'
      }}
    >
      <View
        className={'mc-bg-primary-light'}
        style={{
          minWidth: remCss(80),
          height: remCss(80),
          textAlign: 'center',
          justifyContent: 'center',
          borderRadius: '50%'
        }}
      >
        <MCText primary size={18} label={moment(item.date).format('YYYY')} />
        <MCText
          primary
          size={24}
          weight={500}
          label={moment(item.date).format('MMM').toUpperCase()}
        />
      </View>
      <View
        style={{
          flex: 1,
          flexDirection: 'column',
          gap: remCss(20),
          position: 'relative',
          borderColor: scssVariables['colors-mc-border'],
          borderWidth: 1,
          borderRadius: remCss(10),
          borderStyle: 'solid',
          padding: remCss(20)
        }}
      >
        <View
          style={{
            position: 'absolute',
            top: remCss(32),
            left: remCss(-10),
            backgroundColor: 'white'
          }}
        >
          <SVGIcon
            iconname={'arrow-icon'}
            strokeWidth={0.5}
            fill={scssVariables['colors-mc-border2']}
          />
        </View>
        <MediaGroup images={item.media} template={6} length={2} maxImgHeight={300} aspect={3 / 4} />
        <View style={{ gap: remCss(10), flex: 1 }}>
          <MCText label={item.title} size={20} textLight />
          <MCText label={item.description} size={22} weight={500} heading />
        </View>
      </View>
    </View>
  );

export const GalleryTab = ({ clinic, contentStyle }) => {
  const ref = useRef();
  const screen = useScreenDetail();
  galleryModel.use();

  const gallery = galleryModel.state.gallery;

  useEffect(() => {
    galleryModel.initLoad(clinic, false);
  }, [clinic]);

  const items = galleryModel.items();
  const hasMain = !!gallery?.main?.title;

  let { total = 0 } = galleryModel.getCurrent();

  return (
    <View
      data-testid={'public-gallery-tab'}
      style={{
        ...contentStyle
      }}
    >
      <MCText
        label={LANG_CONST.titles.gallery}
        size={26}
        weight={600}
        heading
        style={{ marginBottom: remCss(39) }}
      />
      <TableLibrary
        template={3}
        noResults={!hasMain && galleryModel.noResults()}
        className={'mc-mb10 mc-grow'}
        showHeader={false}
        topRowExtend={() => {
          return (
            <tr style={{ display: 'flex', gridColumn: '1 / -1' }} ref={ref}>
              {hasMain && (
                <td
                  colSpan={2}
                  style={{
                    flex: 1,
                    marginBottom: remCss(50)
                  }}
                >
                  <View
                    style={{
                      width: '100%',
                      margin: 'auto',
                      maxWidth: 1550,
                      gap: remCss(20)
                    }}
                  >
                    {
                      <>
                        <Splide>
                          {gallery.main.media.map((m, i) => (
                            <SplideSlide key={i}>
                              <FileBox
                                template={2}
                                file={m}
                                list={gallery.main.media}
                                onClose={null}
                                styles={{
                                  width: '100%',
                                  marginLeft: 'auto',
                                  marginRight: 'auto',
                                  maxWidth: remCss(835),
                                  height: remCss(360)
                                }}
                                videoProps={{
                                  width: '100%',
                                  height: '100%'
                                }}
                                onlyPreview={true}
                                showFileName={false}
                              />
                            </SplideSlide>
                          ))}
                        </Splide>
                        <View style={{ gap: remCss(10), flex: 1 }}>
                          <MCText label={gallery.main.title} size={20} textLight />
                          <MCText label={gallery.main.description} size={22} weight={500} heading />
                        </View>
                      </>
                    }
                    <View style={{ flexDirection: 'row', gap: remCss(10), flexWrap: 'wrap' }}>
                      {gallery?.tags.map((tag, index) => {
                        const active = galleryModel.state.tags?.find((t) => t === tag);
                        return (
                          <MCButton
                            primary
                            rounded
                            light
                            label={tag}
                            key={index}
                            dark={active}
                            onClick={() => {
                              galleryModel.setStateSilent({
                                tags: active
                                  ? galleryModel.state.tags?.filter((t) => t !== tag)
                                  : [...galleryModel.state.tags, tag]
                              });
                              galleryModel.refresh();
                            }}
                          />
                        );
                      })}
                    </View>
                  </View>
                </td>
              )}
            </tr>
          );
        }}
        data={items}
        columns={[
          {
            cellStyles: { alignItems: 'flex-start' },
            renderCell: (item, index) => {
              const firstIndex = index * 2;
              if (!items[firstIndex]) return null;
              return (
                <View
                  style={{
                    flexDirection: screen.innerWidth > 900 ? 'row' : 'column',
                    gap: remCss(20),
                    maxWidth: 1550,
                    marginRight: 'auto',
                    marginLeft: 'auto'
                  }}
                >
                  <Item item={items[firstIndex]} />
                  <Item item={items[firstIndex + 1]} />
                </View>
              );
            }
          }
        ]}
        selectedPage={galleryModel.state.pageIndex}
        total={total}
        limit={galleryModel.listLimit}
        onSetPage={(v) => {
          galleryModel.handleChange('pageIndex', v);
          galleryModel.setListState({ items: [] }, galleryModel.getCurrentId(), null, true);
          galleryModel.loadMore();
          ref.current.scrollIntoView({ behavior: 'smooth' });
        }}
      />
    </View>
  );
};
